import { useState, useEffect } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  CreditCard,
  Check,
  MessageCircle,
  Sparkles,
  Zap,
  Award,
  X,
  Loader2,
} from 'lucide-react';
import { PageHeader } from '@/components';
import { SupportModal } from '@/components/SupportModal';
import { useTranslation } from '@crew-at-sea/i18n';
import { useMyCompany } from '@/hooks';
import toast from 'react-hot-toast';
import { paymentsApi } from '@/services/api';
import { useQueryClient } from '@tanstack/react-query';

export const Route = createFileRoute('/_authenticated/subscription/')({
  component: SubscriptionPage,
});

function SubscriptionPage() {
  const { t } = useTranslation('panel');
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const { data: companyResponse, isLoading } = useMyCompany();
  const company = (companyResponse as any)?.data || companyResponse;
  const queryClient = useQueryClient();

  const [modalCategory, setModalCategory] = useState('SUBSCRIPTION');
  const [modalSubject, setModalSubject] = useState('');
  const [modalMessage, setModalMessage] = useState('');

  // PayTR States
  const [paytrModalOpen, setPaytrModalOpen] = useState(false);
  const [paytrToken, setPaytrToken] = useState<string | null>(null);
  const [isInitiating, setIsInitiating] = useState(false);

  useEffect(() => {
    // Check URL parameters for payment result
    const searchParams = new URLSearchParams(window.location.search);
    const paymentStatus = searchParams.get('payment');

    if (paymentStatus === 'success') {
      toast.success(t('subscription.paymentSuccess', { defaultValue: 'Ödeme işleminiz başarıyla tamamlandı! Kredileriniz hesabınıza tanımlandı.' }), { duration: 5000 });
      queryClient.invalidateQueries({ queryKey: ['company', 'me'] });
      // Clear query params
      const url = new URL(window.location.href);
      url.searchParams.delete('payment');
      window.history.replaceState({}, document.title, url.pathname);
    } else if (paymentStatus === 'fail') {
      toast.error(t('subscription.paymentFailed', { defaultValue: 'Ödeme işlemi başarısız oldu veya iptal edildi.' }), { duration: 5000 });
      // Clear query params
      const url = new URL(window.location.href);
      url.searchParams.delete('payment');
      window.history.replaceState({}, document.title, url.pathname);
    }
  }, [queryClient, t]);

  const handlePurchaseRequest = async (packageId: string, packageName: string) => {
    try {
      setIsInitiating(true);
      const session = await paymentsApi.initiate({ packageId });
      const token = (session as any).data?.token || session.token;
      
      if (!token) {
        throw new Error('Token not found in response');
      }

      setPaytrToken(token);
      setPaytrModalOpen(true);
    } catch (error: any) {
      console.error('Payment initiation error:', error);
      toast.error(error?.message || t('subscription.initiateError', { defaultValue: 'Ödeme oturumu başlatılamadı. Lütfen tekrar deneyin.' }));
    } finally {
      setIsInitiating(false);
    }
  };

  const handleContactClick = () => {
    setModalCategory('SUBSCRIPTION');
    setModalSubject('Abonelik / Paket Sorusu');
    setModalMessage('Abonelik planları ve özel teklifler hakkında bilgi almak istiyorum.');
    setSupportModalOpen(true);
  };

  const packages = [
    {
      id: 'single_job',
      name: 'Tekil İlan Paketi',
      price: '50 TL',
      credits: '1 İlan Kredisi',
      features: [
        '1 adet ilan yayınlama hakkı',
        '30 gün boyunca aktif ilan',
        'Aday eşleştirme & filtreleme',
        'Sınırsız başvuru inceleme',
      ],
      color: 'blue',
      popular: false,
    },
    {
      id: 'standard_pkg',
      name: 'Standart Paket',
      price: '300 TL',
      credits: '7 İlan Kredisi',
      features: [
        '7 adet ilan yayınlama hakkı',
        '30 gün boyunca aktif ilanlar',
        'Aday eşleştirme & filtreleme',
        'Sınırsız başvuru inceleme',
        'Fatura desteği',
      ],
      color: 'indigo',
      popular: false,
    },
    {
      id: 'super_pkg',
      name: 'Süper Paket',
      price: '550 TL',
      credits: '14 İlan + 3 Acil İlan Dopingi',
      features: [
        '14 adet ilan yayınlama hakkı',
        '3 adet Acil Dopingi (24 saat sabitleme)',
        '30 gün boyunca aktif ilanlar',
        'Aday eşleştirme & filtreleme',
        'Öncelikli destek',
      ],
      color: 'amber',
      popular: true,
    },
    {
      id: 'urgent_doping',
      name: 'Acil İlan Dopingi',
      price: '25 TL / Doping',
      credits: '1 Doping Kredisi',
      features: [
        'İlanı 24 saat boyunca en üste sabitleme',
        'Arama sonuçlarında "ACİL" rozeti',
        'Daha hızlı başvuru toplama',
      ],
      color: 'red',
      popular: false,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('subscription.title')}
        description={t('subscription.description')}
      />

      {/* Kredi Dashboard */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Normal Kredi */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600">
              <Sparkles className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">{t('subscription.normalCredits', { defaultValue: 'İlan Yayınlama Kredisi' })}</p>
              <h4 className="text-2xl font-bold text-gray-900 mt-1">{isLoading ? '...' : (company?.jobCredits ?? 0)}</h4>
            </div>
          </div>
          <span className="text-xs font-semibold px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full">
            {t('subscription.active', { defaultValue: 'Aktif' })}
          </span>
        </div>
        {/* Acil Kredi */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600">
              <Zap className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">{t('subscription.urgentCredits', { defaultValue: 'Acil İlan Doping Kredisi' })}</p>
              <h4 className="text-2xl font-bold text-gray-900 mt-1">{isLoading ? '...' : (company?.urgentJobCredits ?? 0)}</h4>
            </div>
          </div>
          <span className="text-xs font-semibold px-2.5 py-1 bg-amber-50 text-amber-700 rounded-full">
            {t('subscription.active', { defaultValue: 'Aktif' })}
          </span>
        </div>
      </div>

      {/* Paketler Grid */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">{t('subscription.packages.title', { defaultValue: 'Paket Seçenekleri' })}</h3>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {packages.map((pkg) => (
            <div 
              key={pkg.name} 
              className={`bg-white rounded-2xl border relative flex flex-col justify-between transition-all ${
                pkg.popular 
                  ? 'border-amber-400 ring-2 ring-amber-400/20 shadow-md scale-[1.02]' 
                  : 'border-gray-200 hover:border-gray-300 shadow-sm'
              }`}
            >
              {pkg.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-500 to-yellow-500 text-white text-xs font-bold px-3 py-1 rounded-full uppercase flex items-center gap-1 shadow-sm">
                  <Award className="w-3.5 h-3.5" />
                  {t('subscription.popular', { defaultValue: 'En Popüler' })}
                </span>
              )}
              
              <div className="p-6">
                <div className="mb-4">
                  <h4 className="font-bold text-gray-900 text-lg">{pkg.name}</h4>
                  <p className="text-sm text-gray-500 mt-1">{pkg.credits}</p>
                </div>

                <div className="my-6">
                  <span className="text-3xl font-black text-gray-900">{pkg.price}</span>
                </div>

                <ul className="space-y-3">
                  {pkg.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-gray-600">
                      <Check className="w-4.5 h-4.5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-6 pt-0">
                <button
                  onClick={() => handlePurchaseRequest(pkg.id, pkg.name)}
                  disabled={isInitiating}
                  className={`w-full py-2.5 rounded-xl font-semibold text-sm transition-colors flex items-center justify-center gap-2 ${
                    pkg.popular
                      ? 'bg-amber-500 hover:bg-amber-600 text-white shadow-sm'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                  } disabled:opacity-50`}
                >
                  {isInitiating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('common.loading', { defaultValue: 'İşleniyor...' })}
                    </>
                  ) : (
                    t('subscription.buy', { defaultValue: 'Satın Al' })
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Contact CTA */}
      <div className="bg-gradient-to-r from-maritime-600 to-maritime-700 rounded-2xl p-6 text-white shadow-sm">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="text-lg font-semibold mb-1">{t('subscription.contact.title')}</h3>
            <p className="text-maritime-100 text-sm">
              {t('subscription.contact.description')}
            </p>
          </div>
          <button
            onClick={handleContactClick}
            className="bg-white text-maritime-700 px-6 py-2.5 rounded-xl font-medium hover:bg-maritime-50 transition-colors flex items-center gap-2"
          >
            <MessageCircle className="w-4 h-4" />
            {t('subscription.contact.button')}
          </button>
        </div>
      </div>

      {/* Support Modal */}
      <SupportModal 
        open={supportModalOpen} 
        onOpenChange={setSupportModalOpen} 
        defaultCategory={modalCategory}
        defaultSubject={modalSubject}
        defaultMessage={modalMessage}
      />

      {/* PayTR Payment Modal */}
      {paytrModalOpen && paytrToken && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in"
            onClick={() => setPaytrModalOpen(false)}
          />
          <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl p-4 flex flex-col z-10 animate-scale-in max-h-[90vh]">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-maritime-600" />
                <h3 className="font-bold text-gray-900 text-lg">
                  {t('subscription.paymentTitle', { defaultValue: 'Güvenli Ödeme (PayTR)' })}
                </h3>
              </div>
              <button
                onClick={() => setPaytrModalOpen(false)}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto min-h-[620px] mt-4 relative">
              <iframe
                src={`https://www.paytr.com/odeme/guvenli/${paytrToken}`}
                id="paytriframe"
                frameBorder="0"
                scrolling="yes"
                style={{ width: '100%', height: '620px' }}
                title="PayTR Payment Gateway"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

